import { useMemo, useState } from "react";
import type { IncidentReportConfig } from "../scenario/types";
import type { EvidenceItem } from "../state/store";
import { audio } from "../lib/audio";
import { tx, txr } from "../lib/tx";

/**
 * Episode 11, Stage 3 — the reporting simulator. A capstone "new incident
 * report" form, pre-filled from the learner's OWN Evidence log
 * (`store.evidence()`, passed in by App.tsx since it's runtime state) — the
 * visible payoff of every risky/positive decision made across the whole
 * course. There is no wrong way to report; submitting always advances.
 */
export function IncidentReport({
  config,
  evidence,
  onSubmit,
}: {
  config: IncidentReportConfig;
  evidence: EvidenceItem[];
  onSubmit: () => void;
}) {
  const draft = useMemo(() => {
    if (evidence.length === 0) return config.emptyHint ?? "Nothing unusual noticed yet today.";
    const lines = evidence.map((e, i) => {
      const extra = e.evidence && e.evidence !== e.label ? ` \u2014 ${e.evidence}` : "";
      return `${i + 1}. ${e.label}${extra}.`;
    });
    return ["Timeline of what happened today, as I experienced it:", ...lines].join("\n");
  }, [evidence, config.emptyHint]);

  const [text, setText] = useState(draft);
  const [sent, setSent] = useState(false);

  const riskCount = evidence.filter((e) => e.kind === "risk").length;
  const priority = riskCount >= 2 ? "High" : riskCount === 1 ? "Medium" : "Low";

  const send = () => {
    if (sent) return;
    setSent(true);
    audio.ui("success");
    onSubmit();
  };

  return (
    <div className="irr">
      <div className="irr-panel">
        <div className="irr-headline">{config.headline ?? tx("New incident report")}</div>
        <p className="irr-instructions">
          {config.instructions ??
            tx("IT/Security asked for a written report. Everything below was auto-attached from what actually happened to you today — review it, then send it.")}
        </p>

        <div className="irr-field">
          <label className="irr-label" htmlFor="irr-desc">
            {tx("What happened")}
          </label>
          <textarea
            id="irr-desc"
            className="irr-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={Math.min(10, Math.max(4, evidence.length + 2))}
          />
        </div>

        <div className="irr-evidence">
          <div className="irr-evidence-head">
            <span>{txr("Attached evidence ({length}", { length: evidence.length })})</span>
            <span className={`irr-priority irr-priority--${priority.toLowerCase()}`}>{txr("{priority} priority", { priority })}</span>
          </div>
          <div className="irr-evidence-list">
            {evidence.length === 0 && <p className="irr-evidence-empty">{tx("No prior incidents attached.")}</p>}
            {evidence.map((e) => (
              <div key={e.id} className={`irr-evidence-item irr-evidence-item--${e.kind}`}>
                <span className="irr-evidence-dot" aria-hidden />
                {e.label}
              </div>
            ))}
          </div>
        </div>

        <button type="button" className="btn irr-send" onClick={send} disabled={sent}>
          {sent ? tx("Report sent") : config.submitLabel ?? tx("Send to IT/Security")}
        </button>
      </div>
    </div>
  );
}
